import React, { useReducer, useEffect, useState, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import i18n from 'i18next';

// --- Views & Feature Gates ---
import { Layout } from './components/Layout';
import { LoadingSpinner } from './components/ui/LoadingSpinner';
import { Dashboard } from './views/Dashboard';
import { Tasks } from './views/Tasks';
import { Inventory } from './views/Groceries';
import { Maintenance } from './views/Maintenance';
import { Calendar } from './views/Calendar';
import { HouseholdSettings } from './views/HouseholdSettings';
import { UserSettings } from './views/UserSettings';
import { Contacts } from './views/Contacts';
import { Login } from './views/Login';
import { LandingPage } from './views/LandingPage';
import { PricingPage } from './views/PricingPage';
import { DisclaimerPage, CURRENT_DISCLAIMER_VERSION } from './views/DisclaimerPage';
import { OnboardingLegal, LEGAL_VERSION } from './views/OnboardingLegal';
import { Changelog } from './views/Changelog';
import PrivacyPolicy from './views/PrivacyPolicy';
import TermsOfService from './views/TermsOfService';
import { FeedbackAndPolls } from './views/FeedbackAndPolls';
import { InfluencerDashboard } from './views/InfluencerDashboard';
import { ReferralLanding } from './views/ReferralLanding';
import { MealPlanner } from './views/MealPlanner';
import { DocumentVault } from './views/DocumentVault';
const AIAssistant = lazy(() => import('./views/AIAssistant').then(m => ({ default: m.AIAssistant })));
import { Upgrade } from './views/Upgrade';
import { Explore } from './views/Explore';
import { Pets } from './views/Pets';
import { Vehicles } from './views/Vehicles';
import { VerifyEmail } from './views/VerifyEmail';
import { Bills } from './views/Bills';
const Finance = lazy(() => import('./views/Finance').then(m => ({ default: m.Finance })));
import { CoParenting } from './views/CoParenting';
import { CoParentingProvider } from './contexts/CoParentingContext';
import { MunicipalServices } from './views/MunicipalServices';
import { MunicipalProvider } from './contexts/MunicipalContext';
import { FamilyLocation } from './views/FamilyLocation';
import { LocationProvider } from './contexts/LocationContext';
import { LocationPrivacySettings } from './components/profile/LocationPrivacySettings';
import ElderCare from './views/ElderCare';
import ElderCareProfile from './views/ElderCareProfile';
import { ElderCareProvider } from './contexts/ElderCareContext';
import ElderHomeScreen from './views/ElderHomeScreen';
import { Health } from './views/Health';
import { Appliances } from './views/Appliances';
import { HouseholdSetupWizard } from './components/onboarding/HouseholdSetupWizard';
import { OnboardingProvider } from './context/OnboardingContext';
import { Achievements } from './views/Achievements';
const Analytics = lazy(() => import('./views/Analytics').then(m => ({ default: m.Analytics })));
import { RouteErrorBoundary } from './components/RouteErrorBoundary';
import { FeatureConfigProvider } from './contexts/FeatureConfigContext';
import { AppStateContext } from './contexts/AppStateContext';
import { FeatureGuard } from './components/FeatureGuard';

// --- Components & Utilities ---
import { CookieConsent } from './components/CookieConsent';
import { ToastContainer } from './components/ui/Toast';
import { SearchPortal } from './features/search-engine/components/SearchPortal';

// --- State & Context ---
import { Role, UserProfile, KanbanTask, AppState, CalendarEvent, Household, HouseholdContact, TemplateType } from './types';
import { appReducer, initialState } from './reducers/AppReducer';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { AccessibilityProvider } from './context/AccessibilityContext';
import { LiveRegion } from './components/ui/LiveRegion';
import { KeyboardShortcutsModal } from './components/ui/KeyboardShortcutsModal';
import { SubscriptionStatusBanner } from './components/ui/SubscriptionStatusBanner';

// --- Firebase Infrastructure ---
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  orderBy,
  limit,
  Timestamp,
  DocumentData
} from 'firebase/firestore';

/**
 * 🛠️ UTILITY: Deep Recursive Timestamp Conversion
 * Resolves TS2362: Corrects arithmetic errors by turning Firestore Timestamps into JS Dates.
 */
const deepConvertTimestamps = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return obj;
  if (obj instanceof Timestamp) return obj.toDate();
  if (Array.isArray(obj)) return obj.map(deepConvertTimestamps);

  const result: Record<string, any> = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      result[key] = deepConvertTimestamps(obj[key]);
    }
  }
  return result;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0); // This line scrolls the window to the very top (0,0 coordinates)
  }, [pathname]); // This effect runs every time the pathname (route) changes
  return null; // This component doesn't render any UI
};

const AppContent: React.FC = () => {
  // Explicitly typing the reducer to fix TS2345 (Payload Mismatch)
  const [state, dispatch] = useReducer(appReducer, initialState as AppState);
  const [loading, setLoading] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isShortcutsOpen, setIsShortcutsOpen] = useState(false);
  const [subscribedMunicipalityIds, setSubscribedMunicipalityIds] = useState<string[]>([]);
  const [pendingInviteCode, setPendingInviteCode] = useState<string | null>(null);
  const [contactsActivated, setContactsActivated] = useState(false);
  // Ref to hold the latest personal tasks so household listeners in BOTH scope always read current data
  const personalTasksRef = React.useRef<KanbanTask[]>([]);
  const { setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // 0. 🔗 INVITE CODE PARAM HANDLING
  // Captures ?invite= from URL (survives tab close via localStorage), then redeems once authenticated.
  useEffect(() => {
    // Step 1: capture from URL and persist to localStorage
    const params = new URLSearchParams(window.location.search);
    const urlCode = params.get('invite');
    if (urlCode) {
      const clean = urlCode.toUpperCase().trim();
      const url = new URL(window.location.href);
      url.searchParams.delete('invite');
      window.history.replaceState({}, '', url.toString());
      localStorage.setItem('pendingInviteCode', clean);
    }
    // Step 2: redeem once user is authenticated
    if (!state.currentUser || pendingInviteCode) return;
    const stored = localStorage.getItem('pendingInviteCode');
    if (stored) {
      setPendingInviteCode(stored);
      localStorage.removeItem('pendingInviteCode');
    }
  }, [state.currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // 1. 🌓 THEME SYNC
  useEffect(() => {
    setTheme(state.theme);
  }, [state.theme, setTheme]);

  // 1c. 📒 ACTIVATE contacts listener on first visit to /contacts
  useEffect(() => {
    if (!contactsActivated && location.pathname === '/contacts') {
      setContactsActivated(true);
    }
  }, [location.pathname, contactsActivated]);

  // 1b. 🏙️ SYNC subscribed municipality IDs from active household
  useEffect(() => {
    const activeHousehold = state.households.find(h => h.id === state.currentHouseholdId) || state.households[0];
    setSubscribedMunicipalityIds((activeHousehold as any)?.subscribedMunicipalityIds ?? []);
  }, [state.currentHouseholdId, state.households]);

  // 2. ⌨️ GLOBAL KEY LISTENERS (Ctrl+K search, ? shortcuts, Alt+A accessibility, Alt+1-5 nav)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
        return;
      }
      if (!isTyping && e.key === '?') {
        setIsShortcutsOpen(prev => !prev);
        return;
      }
      if (e.altKey && e.key === 'a') {
        e.preventDefault();
        navigate('/user-settings');
        return;
      }
      if (e.altKey && !isTyping) {
        const navMap: Record<string, string> = { '1': '/dashboard', '2': '/tasks', '3': '/calendar', '4': '/groceries', '5': '/notifications' };
        if (navMap[e.key]) { e.preventDefault(); navigate(navMap[e.key]); }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [navigate]);

  // 3. 👤 AUTH & PROFILE INITIALIZATION
  useEffect(() => {
    let unsubUserDoc: (() => void) | null = null;
    let unsubSubscription: (() => void) | null = null;

    const unsubAuth = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        dispatch({ type: 'SET_EMAIL_VERIFIED', payload: firebaseUser.emailVerified });

        // Subscription status listener
        unsubSubscription = onSnapshot(doc(db, 'subscriptions', firebaseUser.uid), (snap) => {
          if (snap.exists()) {
            const d = snap.data();
            dispatch({
              type: 'SET_SUBSCRIPTION_STATUS',
              payload: {
                tier: (d.tier as any) ?? 'free',
                status: (d.status as any) ?? null,
                cancelAtPeriodEnd: d.cancelAtPeriodEnd ?? false,
                currentPeriodEnd: d.currentPeriodEnd ?? null,
                stripeSubscriptionId: d.stripeSubscriptionId ?? null,
              },
            });
          } else {
            dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: null });
          }
        }, () => {
          // Subscription doc may not exist for new users — non-fatal
          dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: null });
        });

        try {
          const userRef = doc(db, "users", firebaseUser.uid);
          const userSnap = await getDoc(userRef);

          if (!userSnap.exists()) {
            const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const newUser: UserProfile = {
              id: firebaseUser.uid,
              email: firebaseUser.email || '',
              name: firebaseUser.displayName || 'Everyday User',
              avatar: firebaseUser.photoURL || `https://ui-avatars.com/api/?name=${firebaseUser.displayName || 'U'}`,
              role: Role.MEMBER,
              acceptedDisclaimerVersion: '',
              acceptedLegalVersion: '',
              preferredLanguage: i18n.language || 'en',
              timezone: detectedTz,
              autoDetectTz: true,
              activeSparkCategories: ['vocabulary'],
              createdAt: new Date().toISOString(),
            };

            await setDoc(userRef, { ...newUser, createdAt: serverTimestamp() });
          }

          unsubUserDoc = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
              const userData = deepConvertTimestamps(docSnap.data());
              dispatch({ type: 'SET_USER', payload: userData as UserProfile });
              const rawScope = (userData as any).defaultScope;
              if (rawScope === 'PERSONAL' || rawScope === 'SHARED' || rawScope === 'BOTH') {
                dispatch({ type: 'SET_DATA_SCOPE', payload: rawScope });
              }
              setLoading(false);
              dispatch({ type: 'SET_BOOTING', payload: false });
            }
          }, (err) => {
            console.error('[UserDoc onSnapshot]', err.code, err.message);
            setLoading(false);
          });
        } catch (err: any) {
          console.error('[Auth callback]', err?.code, err?.message);
          setLoading(false);
        }
      } else {
        if (unsubUserDoc) unsubUserDoc();
        if (unsubSubscription) unsubSubscription();
        dispatch({ type: 'SET_USER', payload: null });
        dispatch({ type: 'SET_SUBSCRIPTION_STATUS', payload: null });
        setLoading(false);
      }
    });

    return () => {
      unsubAuth();
      if (unsubUserDoc) unsubUserDoc();
      if (unsubSubscription) unsubSubscription();
    };
  }, []);

  // 4. 🛰️ HOUSEHOLD SYNC
  useEffect(() => {
    const user = state.currentUser;
    if (!user || !user.id) return;

    const hQuery = query(collection(db, "households"), where("memberIds", "array-contains", user.id));
    const unsubHouseholds = onSnapshot(hQuery, (snapshot) => {
      if (!snapshot.empty) {
        const hList = snapshot.docs.map(docSnap =>
          deepConvertTimestamps({ id: docSnap.id, ...docSnap.data() })
        ) as Household[];

        dispatch({ type: 'SET_HOUSEHOLDS', payload: hList });

        if (!state.currentHouseholdId && hList.length > 0) {
          dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: hList[0].id });
        }
      }
    });

    return () => unsubHouseholds();
  }, [state.currentUser?.id]);

  // 5. 🛠️ ENGINE LISTENERS (Strictly Typed to fix TS7006/TS2345)
  useEffect(() => {
  const user = state.currentUser;
  const hId = state.currentHouseholdId;
  const currentScope = state.activeDataScope;

  if (!user) {
    dispatch({ type: 'SET_TASKS', payload: [] });
    dispatch({ type: 'SET_INVENTORY', payload: [] });
    dispatch({ type: 'SET_PERSONAL_EVENTS', payload: [] }); // Corrected: SET_CALENDAR_EVENTS to SET_PERSONAL_EVENTS
    dispatch({ type: 'SET_HOUSEHOLD_EVENTS', payload: [] }); // Added: Clear household events
    dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: [] });
    return;
  }

  const unsubscribers: (() => void)[] = [];

  // --- Personal Tasks & Events Listeners ---
  personalTasksRef.current = [];
  if (currentScope === 'PERSONAL' || currentScope === 'BOTH') {
    // Personal Tasks (Action, Grocery, Maintenance)
    const personalTasksQuery = query(
      collection(db, "users", user.id, "personalTasks"),
      orderBy("createdAt", "desc"),
      limit(250)
    );
    const unsubPersonalTasks = onSnapshot(personalTasksQuery, (snap) => {
      personalTasksRef.current = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];
      const currentPersonalTasks = personalTasksRef.current;

      const personalGroceryItems = currentPersonalTasks.filter(t => t.templateType === TemplateType.GROCERY);
      const personalActionTasks = currentPersonalTasks.filter(t => t.templateType === TemplateType.ACTION);
      const personalMaintenanceTasks = currentPersonalTasks.filter(t => t.templateType === TemplateType.MAINTENANCE);

      if (currentScope === 'PERSONAL') {
        dispatch({ type: 'SET_TASKS', payload: personalActionTasks });
        dispatch({ type: 'SET_INVENTORY', payload: personalGroceryItems });
        dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: personalMaintenanceTasks });
      } else if (currentScope === 'BOTH') {
        // If in BOTH scope, personal data will be merged by household listeners.
        // Ensure personal parts are loaded for that merge.
      }
    });
    unsubscribers.push(unsubPersonalTasks);

    // Personal Calendar Events
    const personalEventsQuery = query(collection(db, "users", user.id, "events"), limit(200));
    const unsubPersonalEvents = onSnapshot(personalEventsQuery, (snap) => {
      const events = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as CalendarEvent[];
      dispatch({ type: 'SET_PERSONAL_EVENTS', payload: events });
    });
    unsubscribers.push(unsubPersonalEvents);

  } else {
    // If not personal or both, clear personal-related states from combined view (fallback to empty or household-only if already set)
    dispatch({ type: 'SET_TASKS', payload: (state.tasks ?? []).filter(t => t.scope === 'household') });
    dispatch({ type: 'SET_INVENTORY', payload: (state.inventory ?? []).filter(t => t.scope === 'household') });
    dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: (state.maintenanceTasks ?? []).filter(t => t.scope === 'household') });
    dispatch({ type: 'SET_PERSONAL_EVENTS', payload: [] }); // Explicitly clear personal events
  }

  // --- Household Listeners (if household is active and scope is SHARED or BOTH) ---
  if (hId && (currentScope === 'SHARED' || currentScope === 'BOTH')) {
    // Household Calendar Events
    const unsubHouseholdEvents = onSnapshot(query(collection(db, "households", hId, "events"), limit(200)), (snap) => {
      const events = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as CalendarEvent[];
      dispatch({ type: 'SET_HOUSEHOLD_EVENTS', payload: events }); // Corrected: SET_CALENDAR_EVENTS to SET_HOUSEHOLD_EVENTS
    });
    unsubscribers.push(unsubHouseholdEvents);

    // Household Tasks — ACTION tasks + GROCERY items all stored in /tasks collection
    const unsubHouseholdTasks = onSnapshot(query(collection(db, "households", hId, "tasks"), orderBy("createdAt", "desc"), limit(250)), (snap) => {
      const householdAllTasks = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];
      const householdGroceryItems = householdAllTasks.filter(t => t.templateType === TemplateType.GROCERY && !t.isDeleted);
      const householdActionTasks = householdAllTasks.filter(t => t.templateType !== TemplateType.GROCERY);

      let combinedTasks = householdActionTasks;
      let combinedInventory = householdGroceryItems;
      if (currentScope === 'BOTH') {
        const personalActionTasks = personalTasksRef.current.filter(t => t.templateType === TemplateType.ACTION);
        const personalGroceryItems = personalTasksRef.current.filter(t => t.templateType === TemplateType.GROCERY);
        combinedTasks = [...personalActionTasks, ...householdActionTasks];
        combinedInventory = [...personalGroceryItems, ...householdGroceryItems];
      }
      dispatch({ type: 'SET_TASKS', payload: combinedTasks });
      dispatch({ type: 'SET_INVENTORY', payload: combinedInventory });
    });
    unsubscribers.push(unsubHouseholdTasks);

    // Household Maintenance Tasks (for TemplateType.MAINTENANCE)
    const unsubHouseholdMaint = onSnapshot(query(collection(db, "households", hId, "maintenance"), limit(250)), (snap) => {
      const householdMaintenance = snap.docs.map(d => deepConvertTimestamps({ id: d.id, ...d.data() })) as KanbanTask[];

      let combinedMaintenance = householdMaintenance;
      if (currentScope === 'BOTH') {
        const personalMaintenanceTasks = personalTasksRef.current.filter(t => t.templateType === TemplateType.MAINTENANCE);
        combinedMaintenance = [...personalMaintenanceTasks, ...householdMaintenance];
      }
      dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: combinedMaintenance });
    });
    unsubscribers.push(unsubHouseholdMaint);

  } else {
    // If no household or not SHARED/BOTH, ensure only personal items remain in state
    dispatch({ type: 'SET_HOUSEHOLD_EVENTS', payload: [] }); // Explicitly clear household events if no household or not in shared/both scope
    dispatch({ type: 'SET_TASKS', payload: (state.tasks ?? []).filter(t => t.scope === 'personal') });
    dispatch({ type: 'SET_INVENTORY', payload: (state.inventory ?? []).filter(t => t.scope === 'personal') });
    dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: (state.maintenanceTasks ?? []).filter(t => t.scope === 'personal') });
  }

  return () => {
    unsubscribers.forEach(unsub => unsub());
  };
}, [state.currentUser?.id, state.currentHouseholdId, state.activeDataScope]); // Removed state.personalEvents, state.householdEvents, state.customEvents

// 6. 📞 CONTACTS LISTENER — lazy: only activates after first visit to /contacts
  useEffect(() => {
    const user = state.currentUser;
    const hId = state.currentHouseholdId;
    if (!user?.id) {
      dispatch({ type: 'SET_CONTACTS', payload: [] });
      return;
    }
    if (!contactsActivated) return;

    const unsubscribers: (() => void)[] = [];
    let personalContacts: HouseholdContact[] = [];
    let householdContacts: HouseholdContact[] = [];

    // Personal Contacts Listener
    const personalContactsQuery = query(collection(db, 'users', user.id, 'contacts'), limit(200));
    const unsubPersonal = onSnapshot(personalContactsQuery, (snapshot) => {
      personalContacts = snapshot.docs.map(docSnap =>
        deepConvertTimestamps({ id: docSnap.id, ...docSnap.data() })
      ) as HouseholdContact[];
      dispatch({ type: 'SET_CONTACTS', payload: [...personalContacts, ...householdContacts] });
    });
    unsubscribers.push(unsubPersonal);

    // Household Contacts Listener (if a household is selected)
    if (hId) {
      const householdContactsQuery = query(collection(db, 'households', hId, 'contacts'), limit(200));
      const unsubHousehold = onSnapshot(householdContactsQuery, (snapshot) => {
        householdContacts = snapshot.docs.map(docSnap =>
          deepConvertTimestamps({ id: docSnap.id, ...docSnap.data() })
        ) as HouseholdContact[];
        dispatch({ type: 'SET_CONTACTS', payload: [...personalContacts, ...householdContacts] });
      });
      unsubscribers.push(unsubHousehold);
    } else {
      householdContacts = [];
      dispatch({ type: 'SET_CONTACTS', payload: personalContacts });
    }

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [state.currentUser?.id, state.currentHouseholdId, dispatch, contactsActivated]);

  // 7. 🏷️ CUSTOM GROCERY CATEGORIES LISTENERS
  useEffect(() => {
    const user = state.currentUser;
    const hId = state.currentHouseholdId;
    if (!user?.id) {
      dispatch({ type: 'SET_CUSTOM_GROCERY_CATEGORIES', payload: { scope: 'personal', categories: {} } });
      dispatch({ type: 'SET_CUSTOM_GROCERY_CATEGORIES', payload: { scope: 'household', categories: {} } });
      return;
    }

    const unsubscribers: (() => void)[] = [];

    // Personal Custom Categories Listener
    const personalCategoriesRef = doc(db, "users", user.id, "settings", "customGroceryCategories");
    const unsubPersonalCategories = onSnapshot(personalCategoriesRef, (docSnap) => {
      const categories = docSnap.exists() ? (docSnap.data()?.categories || {}) : {};
      dispatch({ type: 'SET_CUSTOM_GROCERY_CATEGORIES', payload: { scope: 'personal', categories } });
    });
    unsubscribers.push(unsubPersonalCategories);

    // Household Custom Categories Listener (if household exists)
    if (hId) {
      const householdCategoriesRef = doc(db, "households", hId, "settings", "customGroceryCategories");
      const unsubHouseholdCategories = onSnapshot(householdCategoriesRef, (docSnap) => {
        const categories = docSnap.exists() ? (docSnap.data()?.categories || {}) : {};
        dispatch({ type: 'SET_CUSTOM_GROCERY_CATEGORIES', payload: { scope: 'household', categories } });
      });
      unsubscribers.push(unsubHouseholdCategories);
    } else {
      // If no household, ensure household categories are cleared from state
      dispatch({ type: 'SET_CUSTOM_GROCERY_CATEGORIES', payload: { scope: 'household', categories: {} } });
    }

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [state.currentUser?.id, state.currentHouseholdId, dispatch]);

  // 8. 💾 PERSIST CUSTOM GROCERY CATEGORIES TO FIRESTORE (debounced 1.5s)
  useEffect(() => {
    const user = state.currentUser;
    const hId = state.currentHouseholdId;
    if (!user?.id) return;

    const timer = setTimeout(() => {
      const personalCategoriesRef = doc(db, "users", user.id, "settings", "customGroceryCategories");
      setDoc(personalCategoriesRef, { categories: state.customGroceryCategories.personal }, { merge: true })
        .catch(e => console.error("Failed to save personal custom grocery categories:", e));

      if (hId) {
        const householdCategoriesRef = doc(db, "households", hId, "settings", "customGroceryCategories");
        setDoc(householdCategoriesRef, { categories: state.customGroceryCategories.household }, { merge: true })
          .catch(e => console.error("Failed to save household custom grocery categories:", e));
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, [state.customGroceryCategories.personal, state.customGroceryCategories.household, state.currentUser?.id, state.currentHouseholdId]);


  if (loading) {
    return (
      <div className="h-screen w-screen flex flex-col items-center justify-center bg-white dark:bg-slate-950">
        <LoadingSpinner size="lg" className="mb-4" />
        <p className="text-2xs font-black uppercase tracking-[0.3em] text-slate-400">{i18n.t('app.initializing', 'Loading...')}</p>
      </div>
    );
  }

  // --- 🔒 AUTH GATES ---
  if (!state.currentUser) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/ref/:code" element={<ReferralLanding />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  const user = state.currentUser;
  const isLegalPath = location.pathname === '/onboarding-legal';
  const isDisclaimerPath = location.pathname === '/disclaimer';
  const isVerifyEmailPath = location.pathname === '/verify-email';

  const activeHousehold = state.households.find(h => h.id === state.currentHouseholdId) || state.households[0];

  if (user.acceptedLegalVersion !== LEGAL_VERSION && !isLegalPath) {
    return <Navigate to="/onboarding-legal" replace />;
  }

  if (user.acceptedLegalVersion === LEGAL_VERSION && user.acceptedDisclaimerVersion !== CURRENT_DISCLAIMER_VERSION && !isDisclaimerPath) {
    return <Navigate to="/disclaimer" replace />;
  }

  // Email verification: 48h grace period from signup, then hard-blocked until verified.
  // emailVerified comes from Firebase Auth (state.emailVerified), not the Firestore profile —
  // it can flip true client-side via auth.currentUser.reload() with no Firestore write.
  const signupAgeHours = user.createdAt ? (Date.now() - new Date(user.createdAt).getTime()) / 3600000 : 0;
  const needsEmailVerification = !state.emailVerified && signupAgeHours > 48;
  if (needsEmailVerification && !isLegalPath && !isDisclaimerPath && !isVerifyEmailPath) {
    return <Navigate to="/verify-email" replace />;
  }

  // First-run wizard: user has completed legal/disclaimer but has no households yet
  if (!state.isBooting && state.households.length === 0 && !isLegalPath && !isDisclaimerPath) {
    return (
      <OnboardingProvider userId={state.currentUser.id}>
        <HouseholdSetupWizard state={state} dispatch={dispatch} pendingInviteCode={pendingInviteCode} />
      </OnboardingProvider>
    );
  }

  if (state.currentUser?.elderModeEnabled && !isLegalPath && !isDisclaimerPath) {
    return (
      <ElderCareProvider userId={state.currentUser?.id}>
        <AppStateContext.Provider value={{ state, dispatch }}>
          <ElderHomeScreen />
        </AppStateContext.Provider>
      </ElderCareProvider>
    );
  }

  return (
    <AccessibilityProvider
      prefs={state.currentUser?.accessibility ?? {}}
      onUpdate={(changes) => {
        dispatch({ type: 'UPDATE_ACCESSIBILITY', payload: changes });
        if (state.currentUser?.id) {
          setDoc(doc(db, 'users', state.currentUser.id), { accessibility: { ...state.currentUser.accessibility, ...changes } }, { merge: true })
            .catch(e => console.error('Failed to save accessibility prefs:', e));
        }
      }}
    >
    <OnboardingProvider userId={state.currentUser?.id ?? null}>
    <FeatureConfigProvider>
    <MunicipalProvider subscribedIds={subscribedMunicipalityIds}>
    <CoParentingProvider householdId={activeHousehold?.id} linkId={(activeHousehold as any)?.coParentingLinkId}>
    <ElderCareProvider userId={state.currentUser?.id}>
    <LocationProvider userId={state.currentUser?.id} householdId={state.currentHouseholdId}>
    <AppStateContext.Provider value={{ state, dispatch }}>
    <div className="min-h-screen bg-white dark:bg-slate-950 transition-colors duration-300 flex flex-col">
      <LiveRegion />
      <SubscriptionStatusBanner subscriptionStatus={state.subscriptionStatus} />
      <ScrollToTop />

      {isShortcutsOpen && <KeyboardShortcutsModal onClose={() => setIsShortcutsOpen(false)} />}

      <SearchPortal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        state={state}
      />

      <Routes>
        <Route path="/onboarding-legal" element={<RouteErrorBoundary><OnboardingLegal user={state.currentUser} dispatch={dispatch} /></RouteErrorBoundary>} />
        <Route path="/disclaimer" element={<RouteErrorBoundary><DisclaimerPage state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
        <Route path="/verify-email" element={<RouteErrorBoundary><VerifyEmail dispatch={dispatch} /></RouteErrorBoundary>} />

        <Route path="*" element={
          <Layout
            user={state.currentUser}
            currentHousehold={activeHousehold}
            notifications={state.notifications}
            dispatch={dispatch}
            onLogout={async () => {
              await signOut(auth);
              dispatch({ type: 'SET_USER', payload: null });
              dispatch({ type: 'SET_CURRENT_HOUSEHOLD', payload: null });
              dispatch({ type: 'SET_TASKS', payload: [] });
              dispatch({ type: 'SET_INVENTORY', payload: [] });
              dispatch({ type: 'SET_MAINTENANCE_TASKS', payload: [] });
              dispatch({ type: 'SET_CONTACTS', payload: [] });
              dispatch({ type: 'SET_PERSONAL_EVENTS', payload: [] });
              dispatch({ type: 'SET_HOUSEHOLD_EVENTS', payload: [] });
              navigate('/login');
            }}
            theme={state.theme}
            onToggleTheme={() => dispatch({ type: 'TOGGLE_THEME' })}
            onSetTheme={(t) => {
              dispatch({ type: 'SET_THEME', payload: t });
              if (state.currentUser?.id) {
                setDoc(doc(db, 'users', state.currentUser.id), { theme: t }, { merge: true })
                  .catch(e => console.error('Failed to save theme:', e));
              }
            }}
          >
            <Routes>
              <Route path="/dashboard" element={<RouteErrorBoundary routeName="Dashboard"><Dashboard state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/tasks" element={<FeatureGuard featureId="tasks"><RouteErrorBoundary routeName="Tasks"><Tasks state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/groceries" element={<FeatureGuard featureId="inventory"><RouteErrorBoundary routeName="Grocery"><Inventory state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/calendar" element={<FeatureGuard featureId="calendar"><RouteErrorBoundary routeName="Calendar"><Calendar state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/maintenance" element={<FeatureGuard featureId="maintenance"><RouteErrorBoundary routeName="Maintenance"><Maintenance state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/appliances" element={<FeatureGuard featureId="maintenance"><RouteErrorBoundary routeName="Appliances"><Suspense fallback={<LoadingSpinner />}><Appliances state={state} dispatch={dispatch} /></Suspense></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/user-settings" element={<RouteErrorBoundary routeName="UserSettings"><UserSettings user={state.currentUser} state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/settings" element={<RouteErrorBoundary routeName="Settings"><HouseholdSettings state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/changelog" element={<Changelog />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
              <Route path="/contacts" element={<FeatureGuard featureId="contacts"><RouteErrorBoundary routeName="Contacts"><Contacts state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/feedback" element={<FeedbackAndPolls />} />
              <Route path="/influencer" element={<InfluencerDashboard />} />
              <Route path="/meal-planner" element={<FeatureGuard featureId="meal-planner"><RouteErrorBoundary routeName="Meal Planner"><MealPlanner state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/documents" element={<FeatureGuard featureId="documents"><RouteErrorBoundary routeName="Documents"><DocumentVault state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/assistant" element={<FeatureGuard featureId="assistant"><RouteErrorBoundary routeName="AI Assistant"><Suspense fallback={<LoadingSpinner />}><AIAssistant state={state} dispatch={dispatch} /></Suspense></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/upgrade" element={<RouteErrorBoundary routeName="Upgrade"><Upgrade state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/pets" element={<FeatureGuard featureId="pets"><RouteErrorBoundary routeName="Pets"><Pets state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/vehicles" element={<FeatureGuard featureId="vehicles"><RouteErrorBoundary routeName="Vehicles"><Vehicles state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/bills" element={<Navigate to="/finance" replace />} />
              <Route path="/health" element={<FeatureGuard featureId="health"><RouteErrorBoundary routeName="Health"><Health state={state} dispatch={dispatch} /></RouteErrorBoundary></FeatureGuard>} />
              <Route path="/explore" element={<RouteErrorBoundary routeName="Explore"><Explore state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/achievements" element={<RouteErrorBoundary routeName="Achievements"><Achievements state={state} /></RouteErrorBoundary>} />
              <Route path="/analytics" element={<RouteErrorBoundary routeName="Analytics"><Suspense fallback={<LoadingSpinner />}><Analytics state={state} dispatch={dispatch} /></Suspense></RouteErrorBoundary>} />
              <Route path="/finance" element={<RouteErrorBoundary routeName="Finance"><Suspense fallback={<LoadingSpinner />}><Finance state={state} dispatch={dispatch} /></Suspense></RouteErrorBoundary>} />
              <Route path="/members" element={<Navigate to="/settings" replace />} />
              <Route path="/property" element={<Navigate to="/finance" replace />} />
              <Route path="/co-parenting" element={<RouteErrorBoundary routeName="Co-Parenting"><CoParenting state={state} dispatch={dispatch} /></RouteErrorBoundary>} />
              <Route path="/municipal-services" element={<RouteErrorBoundary routeName="Municipal Services"><MunicipalServices householdId={activeHousehold?.id ?? ''} subscribedMunicipalityIds={subscribedMunicipalityIds} onSubscribe={setSubscribedMunicipalityIds} /></RouteErrorBoundary>} />
              <Route path="/elder-care" element={<RouteErrorBoundary routeName="Elder Care"><ElderCare /></RouteErrorBoundary>} />
              <Route path="/elder-care/:profileId" element={<RouteErrorBoundary routeName="Elder Care Profile"><ElderCareProfile /></RouteErrorBoundary>} />
              <Route path="/family-location" element={<RouteErrorBoundary routeName="Family Location"><FamilyLocation /></RouteErrorBoundary>} />
              <Route path="/profile/location-settings" element={<RouteErrorBoundary routeName="Location Privacy"><LocationPrivacySettings /></RouteErrorBoundary>} />
            </Routes>
          </Layout>
        } />
      </Routes>

    </div>
    </AppStateContext.Provider>
    </LocationProvider>
    </ElderCareProvider>
    </CoParentingProvider>
    </MunicipalProvider>
    </FeatureConfigProvider>
    </OnboardingProvider>
    </AccessibilityProvider>
  );
};

const App: React.FC = () => (
  <ThemeProvider>
    <AppContent />
    <ToastContainer />
  </ThemeProvider>
);

export default App;
